import React, { useState } from 'react';
import Modal from '../common/Modal';
import Button from '../common/Button';
import StatusBadge from '../common/StatusBadge';
import Table from '../common/Table';
import {
  DocumentIcon,
  ArrowDownTrayIcon,
  DocumentTextIcon,
  CalendarIcon,
  BuildingOfficeIcon,
  CheckCircleIcon,
  HandThumbUpIcon,
  InformationCircleIcon,
  ClockIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';

import { formatDateTime } from '../../utils/dateUtils';
import { capitalizeWords } from '../../utils/formatUtils';

const TabButton = ({ icon: Icon, label, isActive, onClick }) => (
  <button
    className={`flex items-center gap-2 px-4 py-3 text-sm font-medium border-b-2 transition-colors duration-200 ${isActive
      ? 'border-blue-600 text-blue-600'
      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
      }`}
    onClick={onClick}
  >
    <span
      className={
        isActive
          ? 'p-1.5 rounded-full bg-blue-50 text-blue-600'
          : 'p-1.5 rounded-full text-gray-400'
      }
    >
      <Icon className="h-5 w-5" />
    </span>
    {label}
  </button>
);

function FileRow({ name, url }) {
  const [hovered, setHovered] = useState(false);
  const canOpen = Boolean(url);

  const handleOpen = () => {
    if (!url) return;
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  return (
    <div
      className={`flex items-center justify-between px-4 py-3.5 rounded-xl border transition-all duration-200 ${hovered ? 'bg-blue-50 border-blue-200 shadow-sm' : 'bg-white border-gray-200'
        } ${canOpen ? 'cursor-pointer' : ''}`}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      onClick={handleOpen}
      role={canOpen ? 'button' : undefined}
      tabIndex={canOpen ? 0 : undefined}
      onKeyDown={(e) => {
        if (!canOpen) return;
        if (e.key === 'Enter' || e.key === ' ') handleOpen();
      }}
    >
      <div className="flex items-center gap-3 min-w-0">
        <div className="flex-shrink-0">
          <DocumentTextIcon className="h-[22px] w-[22px] text-blue-600" />
        </div>
        <span className="text-blue-600 font-medium text-sm hover:underline truncate">
          {name}
        </span>
      </div>
      <a
        href={url || '#'}
        download
        onClick={(e) => {
          e.stopPropagation();
          if (!url) e.preventDefault();
        }}
        className={`p-1.5 rounded-lg transition-all duration-150 ${hovered ? 'text-blue-600 bg-blue-100' : 'text-blue-400 hover:text-blue-600'
          } ${url ? '' : 'pointer-events-none opacity-50'}`}
        title="Download"
        aria-label="Download"
      >
        <ArrowDownTrayIcon className="h-5 w-5" />
      </a>
    </div>
  );
}

function EmptyState({ title, description }) {
  return (
    <div className="bg-gray-50 rounded-2xl p-8">
      <div className="rounded-2xl p-10 text-center border border-gray-100">
        <DocumentIcon className="h-14 w-14 mx-auto mb-4 text-gray-300" />
        <p className="text-lg font-semibold text-gray-800">{title}</p>
        {description ? <p className="mt-2 text-sm text-gray-500">{description}</p> : null}
      </div>
    </div>
  );
}

const ViewApplicationModal = ({ isOpen, onClose, application }) => {
  const [activeTab, setActiveTab] = useState('information');
  const [activityPage, setActivityPage] = useState(1);

  if (!application) return null;

  // Support both "application-centric" objects and "permit-centric" objects
  const coreApplication = application?.application || application;

  const businessPermit =
    coreApplication?.business_permit ||
    application?.business_permit || {
      permit_number: application?.permit_number,
      issued_date: application?.issued_date,
      status: application?.status,
      valid_from: application?.valid_from,
      valid_until: application?.valid_until,
    };

  const auditLogs = Array.isArray(application?.audit_logs)
    ? application.audit_logs
    : Array.isArray(coreApplication?.audit_logs)
      ? coreApplication.audit_logs
      : [];

  const tabs = [
    { id: 'information', label: 'Information', icon: InformationCircleIcon },
    { id: 'documents', label: 'Documents', icon: DocumentIcon },
    { id: 'endorsement', label: 'Endorsement', icon: HandThumbUpIcon },
    { id: 'validity', label: 'Validity', icon: CheckCircleIcon },
    { id: 'activity', label: 'Activity Logs', icon: ClockIcon },
  ];

  const renderInformationTab = () => (
    <div className="bg-gray-50 rounded-2xl p-8">
      <div className="grid grid-cols-2 gap-x-12 gap-y-8">
        <div>
          <label className="block text-sm font-medium text-gray-500">Application Type</label>
          <p className="mt-1 text-sm text-gray-900">
            {capitalizeWords(coreApplication?.application_type)}
          </p>
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-500">Permit Year</label>
          <p className="mt-1 text-sm text-gray-900">{coreApplication?.permit_year}</p>
        </div>
        <div className="col-span-2">
          <label className="block text-sm font-medium text-gray-500">Business Name</label>
          <p className="mt-1 text-sm text-gray-900 font-medium">
            {coreApplication?.business?.business_name ||
              coreApplication?.business_name ||
              application?.business?.name}
          </p>
        </div>
        <div className="col-span-2">
          <label className="block text-sm font-medium text-gray-500">Remarks</label>
          <p className="mt-1 text-sm text-gray-900">{coreApplication?.remarks}</p>
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-500">Reference No.</label>
          <p className="mt-1 text-sm text-gray-900 font-medium">
            {coreApplication?.application_ref_no}
          </p>
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-500">Status</label>
          <div className="mt-1">
            <StatusBadge status={coreApplication?.status || application?.status} />
          </div>
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-500">Submitted At</label>
          <p className="mt-1 text-sm text-gray-900">
            {formatDateTime(coreApplication?.submitted_at || application?.submitted_at)}
          </p>
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-500">Created At</label>
          <p className="mt-1 text-sm text-gray-900">
            {formatDateTime(coreApplication?.created_at || application?.created_at)}
          </p>
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-500">Updated At</label>
          <p className="mt-1 text-sm text-gray-900">
            {formatDateTime(coreApplication?.updated_at || application?.updated_at)}
          </p>
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-500">Created By</label>
          <p className="mt-1 text-sm text-gray-900">
            {coreApplication?.created_by?.name ||
              coreApplication?.creator?.name ||
              application?.created_by?.name}
          </p>
        </div>
      </div>
    </div>
  );

  const renderDocumentsTab = () => (
    <div className="bg-gray-50 rounded-2xl p-8">
      <div className="space-y-6">
        {(() => {
          const docs = Array.isArray(coreApplication?.documents) ? coreApplication.documents : [];
          if (docs.length === 0) {
            return (
              <div className="bg-white rounded-2xl p-10 text-center border border-gray-100">
                <DocumentIcon className="h-14 w-14 mx-auto mb-4 text-gray-300" />
                <p className="text-lg font-semibold text-gray-800">No documents available.</p>
                <p className="mt-2 text-sm text-gray-500">Uploaded documents will appear here once provided.</p>
              </div>
            );
          }

          const grouped = docs.reduce((acc, d) => {
            const key = d?.requirement || 'Documents';
            if (!acc[key]) acc[key] = [];
            acc[key].push(d);
            return acc;
          }, {});

          return Object.entries(grouped).map(([section, items]) => (
            <div key={section} className="space-y-2">
              <h3 className="text-sm font-semibold text-gray-600 tracking-wide">{section}</h3>
              <div className="space-y-2">
                {items.map((doc) => (
                  <FileRow
                    key={doc?.doc_id ?? `${doc?.requirement_id ?? 'req'}-${doc?.file?.file_name ?? doc?.file?.url ?? Math.random()}`}
                    name={doc?.file?.file_name || 'Unnamed file'}
                    url={doc?.file?.url}
                  />
                ))}
              </div>
            </div>
          ));
        })()}
      </div>
    </div>
  );

  const renderEndorsementTab = () => {
    const endorsement = coreApplication?.endorsement;
    const hasEndorsementData = Boolean(
      endorsement && (endorsement.status || endorsement.endorser_name || endorsement.endorsed_at || endorsement.remarks)
    );

    if (!hasEndorsementData) {
      return (
        <EmptyState
          title="No endorsement records available."
          description="Endorsements will appear here once applications are reviewed."
        />
      );
    }

    return (
      <div className="bg-gray-50 rounded-2xl p-8">
        <div className="space-y-4">
          <div className="grid grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-500">Endorsement Status</label>
              <div className="mt-1">
                <StatusBadge status={endorsement?.status} />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-500">Endorsed by</label>
              <p className="mt-1 text-sm text-gray-900 font-medium">{endorsement?.endorsed_by?.name || '-'}</p>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-500">Endorsement Date</label>
              <p className="mt-1 text-sm text-gray-900">{formatDateTime(endorsement?.endorsement_date)}</p>
            </div>
            <div className="col-span-3">
              <label className="block text-sm font-medium text-gray-500">Remarks</label>
              <p className="text-sm text-gray-700 leading-relaxed">{endorsement?.remarks || '-'}</p>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const renderValidityTab = () => {
    const permit = businessPermit;
    const hasPermitData = Boolean(
      permit && (permit.permit_number || permit.issued_date || permit.status || permit.valid_from || permit.valid_until)
    );

    if (!hasPermitData) {
      return (
        <EmptyState
          title="No permit validity details available."
          description="Validity information will appear here once a permit is issued."
        />
      );
    }

    return (
      <div className="bg-gray-50 rounded-2xl p-8">
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="col-span-2">
              <label className="block text-sm font-medium text-gray-500">Permit Number</label>
              <p className="mt-1 text-sm text-gray-900 font-medium">{permit?.permit_number || permit?.permit_no || '-'}</p>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-500">Issued Date</label>
              <p className="mt-1 text-sm text-gray-900">{formatDateTime(permit?.issued_date)}</p>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-500">Permit Status</label>
              <div className="mt-1">
                <StatusBadge status={permit?.status} />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-500">Valid From</label>
              <p className="mt-1 text-sm text-gray-900">{formatDateTime(permit?.valid_from)}</p>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-500">Valid Until</label>
              <p className="mt-1 text-sm text-gray-900">{formatDateTime(permit?.valid_until)}</p>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const renderActivityTab = () => {
    const logs = auditLogs;
    const perPage = 5;

    if (logs.length === 0) {
      return (
        <EmptyState
          title="No activity records available."
          description="Activity logs will appear here once actions are performed on the application."
        />
      );
    }

    const safeParseJson = (value) => {
      if (value === null || value === undefined) return value;
      if (typeof value === 'object') return value;
      if (typeof value !== 'string') return value;
      try {
        return JSON.parse(value);
      } catch {
        return value;
      }
    };

    const rows = logs.map((log) => {
      const oldValues = safeParseJson(log.old_values);
      const newValues = safeParseJson(log.new_values);

      return {
        id: log.id,
        applicationType: capitalizeWords(coreApplication?.application_type),
        action: log.action,
        date: formatDateTime(log.created_at),
        oldValue: JSON.stringify(oldValues, null, 2),
        newValue: JSON.stringify(newValues, null, 2),
      };
    });

    const totalPages = Math.ceil(rows.length / perPage);
    const currentPage = Math.min(activityPage, totalPages || 1);
    const startIndex = (currentPage - 1) * perPage;
    const paginatedRows = rows.slice(startIndex, startIndex + perPage);

    const columns = [
      {
        key: 'applicationType',
        label: 'Application Type'
      },
      {
        key: 'action',
        label: 'Action'
      },
      {
        key: 'date',
        label: 'Activity Date'
      },
      {
        key: 'oldValue',
        label: 'Old Value',
        render: (value) => {
          const [expanded, setExpanded] = React.useState(false);
          const preRef = React.useRef(null);
          const [overflow, setOverflow] = React.useState(false);

          React.useEffect(() => {
            if (preRef.current) {
              setOverflow(preRef.current.scrollHeight > preRef.current.clientHeight);
            }
          }, [value]);

          return (
            <div className="min-w-[100px] max-w-[200px]">
              <pre
                ref={preRef}
                className={`whitespace-pre-wrap break-words font-mono text-xs ${!expanded ? 'line-clamp-3' : ''
                  }`}
              >
                {value}
              </pre>
              {overflow && (
                <button
                  onClick={() => setExpanded(!expanded)}
                  className="mt-1 text-blue-600 text-xs font-medium hover:underline"
                >
                  {expanded ? 'View Less' : 'View More'}
                </button>
              )}
            </div>
          );
        }
      },
      {
        key: 'newValue',
        label: 'New Value',
        render: (value) => {
          const [expanded, setExpanded] = React.useState(false);
          const preRef = React.useRef(null);
          const [overflow, setOverflow] = React.useState(false);

          React.useEffect(() => {
            if (preRef.current) {
              setOverflow(preRef.current.scrollHeight > preRef.current.clientHeight);
            }
          }, [value]);

          return (
            <div className="min-w-[100px] max-w-[200px]">
              <pre
                ref={preRef}
                className={`whitespace-pre-wrap break-words font-mono text-xs ${!expanded ? 'line-clamp-3' : ''
                  }`}
              >
                {value}
              </pre>
              {overflow && (
                <button
                  onClick={() => setExpanded(!expanded)}
                  className="mt-1 text-blue-600 text-xs font-medium hover:underline"
                >
                  {expanded ? 'View Less' : 'View More'}
                </button>
              )}
            </div>
          );
        }
      }
    ];

    return (
      <div className="bg-gray-50 rounded-2xl p-6">
        <Table
          columns={columns}
          data={paginatedRows}
          noContainer
          hideHeader={false}
          pagination={{
            currentPage: currentPage,
            lastPage: totalPages,
            perPage: perPage,
            total: rows.length
          }}
          onPageChange={setActivityPage}
          paginationProps={{ showSizeSelector: false }}
        />
      </div>
    );
  };

  const renderTabContent = () => {
    switch (activeTab) {
      case 'information':
        return renderInformationTab();
      case 'documents':
        return renderDocumentsTab();
      case 'endorsement':
        return renderEndorsementTab();
      case 'validity':
        return renderValidityTab();
      case 'activity':
        return renderActivityTab();
      default:
        return renderInformationTab();
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="6xl" showCloseButton={false}>
      <div className="flex items-center justify-between mb-6 -mx-6 px-6 pb-4 border-b border-gray-100">
        <div className="flex items-center gap-4">
          <span className="px-4 py-2 rounded-lg bg-blue-600 text-white text-sm font-semibold">
            {coreApplication?.application_ref_no}
          </span>
          <h2 className="text-xl font-semibold text-gray-900">Business Permit Details</h2>
        </div>

        <div className="flex items-center gap-3">
          <StatusBadge status={coreApplication?.status || application?.status} />
          <button
            type="button"
            onClick={onClose}
            className="p-2 rounded-full hover:bg-gray-100"
            aria-label="Close"
          >
            <XMarkIcon className="h-5 w-5 text-gray-400" />
          </button>
        </div>
      </div>

      {/* Tab Navigation */}
      <div className="border-b border-gray-200 mb-6 -mx-6 px-6">
        <nav className="flex space-x-8">
          {tabs.map((tab) => {
            const Icon = tab.icon;
            return (
              <TabButton
                key={tab.id}
                icon={Icon}
                label={tab.label}
                isActive={activeTab === tab.id}
                onClick={() => setActiveTab(tab.id)}
              />
            );
          })}
        </nav>
      </div>

      {/* Tab Content */}
      <div className="min-h-[400px]">
        {renderTabContent()}
      </div>

      {/* Footer */}
      <div className="flex justify-end gap-3 mt-6 pt-4 border-t border-gray-200">
        <Button variant="primary" onClick={onClose}>
          Close
        </Button>
      </div>
    </Modal>
  );
};

export default ViewApplicationModal;
